/**
 * Fee tax helpers shared by the payment-collection surfaces
 * (payment-console, CollectPaymentModal — both post to
 * POST /enrollment/:id/collect-payment).
 *
 * Two conventions exist in this codebase and they are NOT interchangeable:
 *
 *  - tax-EXCLUSIVE  `base * pct/100`        — the amount in hand is a plan/base
 *    figure and tax is added on top. Used by the gateway path
 *    (nodefork initiate.controller.ts) and by the grid to render what is due.
 *  - tax-INCLUSIVE  `gross * pct/(100+pct)` — the amount in hand is the GROSS
 *    the student handed over and the tax must be carved back out. Used here,
 *    on the cashier's entered amount.
 *
 * Applying the exclusive formula to a gross amount under-credits the base and
 * leaves a residual that never clears (it decays by `1 - 0.82*1.18` = 3.24% per
 * payment at 18%, so the installment can never be closed). These are ports of
 * PaymentUtil.getTaxes() / getTaxesOnFee(), which kept the two apart by name.
 */

/**
 * Money rounding — precision comes from the single knob `MONEY_DECIMALS`
 * (src/config/money.ts). Re-exported here so existing importers keep working.
 */
import { roundMoney, MONEY_DECIMALS, MONEY_STEP } from "../../../config/money";

export { roundMoney, MONEY_DECIMALS, MONEY_STEP };

/** @deprecated import `roundMoney` from config/money. Kept as an alias. */
export const round2 = roundMoney;

export interface GrossSplit {
  /** Base (pre-tax) portion of the gross. */
  base: number;
  /** Tax portion carved out of the gross. */
  tax: number;
}

/**
 * Carve tax OUT of a tax-INCLUSIVE gross.
 * `taxPct` is the head's TOTAL tax percentage (e.g. 18 for CGST 9 + SGST 9).
 */
export function splitGross(gross: number, taxPct: number): GrossSplit {
  const g = Number(gross) || 0;
  const p = Number(taxPct) || 0;
  if (g <= 0 || p <= 0) return { base: round2(g), tax: 0 };
  const tax = round2((g * p) / (100 + p));
  return { base: round2(g - tax), tax };
}

/** Gross up a tax-EXCLUSIVE base amount — what the grid renders as due. */
export function grossUp(base: number, taxPct: number): number {
  const b = Number(base) || 0;
  const p = Number(taxPct) || 0;
  return round2(b + (b * p) / 100);
}

export interface PaymentHead {
  installmentId: number;
  installmentHeadId: number;
  /** BASE portion — the backend records this verbatim, it does not re-derive. */
  amount: number;
  /** Tax portion — the backend distributes this across the head's tax heads. */
  taxAmount: number;
}

/**
 * Turn the grid's `amounts` map (keyed "installmentId-headId", values are the
 * GROSS the cashier typed) into the collect-payment `heads[]` payload, with the
 * base/tax split resolved here on the screen that already knows both figures.
 *
 * The backend deliberately does no tax arithmetic on these amounts.
 */
export function buildPaymentHeads(
  amounts: Record<string, number>,
  installments: Array<{ installmentId: number; heads?: any[] }>,
): PaymentHead[] {
  const heads: PaymentHead[] = [];
  for (const [key, gross] of Object.entries(amounts)) {
    if (!gross || gross <= 0) continue;
    const [instId, headId] = key.split("-").map(Number);
    const inst = installments.find((i) => i.installmentId === instId);
    const head = (inst?.heads || []).find((h: any) => h.headId === headId);
    const { base, tax } = splitGross(gross, Number(head?.headTaxPerc) || 0);
    heads.push({
      installmentId: instId,
      installmentHeadId: headId,
      amount: base,
      taxAmount: tax,
    });
  }
  return heads;
}
